import { useState } from 'react'
import { CheckCircle, XCircle, ExternalLink, ChevronDown } from 'lucide-react'
import { normalizeUrl } from '../lib/reportUtils'

const SEVERITY_STOPS = ['#9c2128', '#de6433', '#39774f']

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// Walks the stops above for t in [0, 1] (red → orange → green).
function interpolateSeverity(t) {
  const clamped = Math.min(1, Math.max(0, t))
  const span = 1 / (SEVERITY_STOPS.length - 1)
  const i = Math.min(SEVERITY_STOPS.length - 2, Math.floor(clamped / span))
  const local = (clamped - i * span) / span
  const from = hexToRgb(SEVERITY_STOPS[i])
  const to = hexToRgb(SEVERITY_STOPS[i + 1])
  const [r, g, b] = from.map((c, k) => Math.round(c + (to[k] - c) * local))
  return {
    color: `rgb(${r} ${g} ${b})`,
    backgroundColor: `rgb(${r} ${g} ${b} / 0.1)`,
    borderColor: `rgb(${r} ${g} ${b} / 0.3)`,
  }
}

// Beyond one stddev from the median the color is flat red / green; inside that
// band it slides continuously from one to the other.
function getScoreSeverity(score, median, stddev) {
  if (score > median + stddev) return interpolateSeverity(1)
  if (score < median - stddev) return interpolateSeverity(0)
  const t = stddev > 0 ? (score - (median - stddev)) / (2 * stddev) : 0.5
  return interpolateSeverity(t)
}

function groupByHostname(urls) {
  // Collapse tracking-parameter variants first, the same way SourcesPanorama does.
  const merged = {}
  for (const [rawUrl, count] of Object.entries(urls)) {
    const url = normalizeUrl(rawUrl)
    merged[url] = (merged[url] || 0) + count
  }

  const groups = {}
  for (const [url, count] of Object.entries(merged)) {
    let host
    try { host = new URL(url).hostname } catch { host = url }
    if (!groups[host]) groups[host] = []
    groups[host].push({ url, count })
  }
  for (const host of Object.keys(groups)) {
    groups[host].sort((a, b) => b.count - a.count)
  }
  return Object.entries(groups).sort(([, a], [, b]) => {
    const sumA = a.reduce((s, e) => s + e.count, 0)
    const sumB = b.reduce((s, e) => s + e.count, 0)
    return sumB - sumA
  })
}

function HostGroup({ host, entries }) {
  const [open, setOpen] = useState(false)
  const total = entries.reduce((s, e) => s + e.count, 0)

  const countBadge = (n) => (
    <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#4e6498]/10 text-[#4e6498] font-bold text-[0.7rem]">
      {n}
    </span>
  )

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-1.5 border border-[#77726e]/25 bg-[#f6f3ee] hover:bg-[#edeae4] hover:border-[#4e6498]/40 transition text-xs text-[#2f3126]"
      >
        {countBadge(total)}
        <span className="flex-1 text-left font-medium">{host}</span>
        {entries.length > 1 && (
          <span className="text-[#77726e] text-[0.7rem]">{entries.length} pages</span>
        )}
        <ChevronDown
          size={12}
          className={`shrink-0 text-[#77726e] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-x border-b border-[#77726e]/25 divide-y divide-[#77726e]/10">
          {entries.map(({ url, count }) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={url}
              className="flex items-center gap-2 pl-10 pr-3 py-1.5 bg-white hover:bg-[#f6f3ee] transition text-xs text-[#4e6498] hover:text-[#2f3126] group"
            >
              {countBadge(count)}
              <span className="truncate flex-1">{url.length > 55 ? url.slice(0, 55) + '…' : url}</span>
              <ExternalLink size={10} className="shrink-0 text-[#77726e]" />
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

function UrlPopup({ urls, llmName, category, onClose }) {
  const groups = groupByHostname(urls)
  // Counted off the grouped result, not the raw map: normalizeUrl may have merged
  // tracking-parameter variants, and the header must agree with the list below it.
  const totalPages = groups.reduce((s, [, entries]) => s + entries.length, 0)
  const totalRefs = groups.reduce(
    (s, [, entries]) => s + entries.reduce((n, e) => n + e.count, 0), 0
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-xl max-w-lg w-full mx-4 max-h-[32rem] flex flex-col border border-[#77726e]/30"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-[#77726e]/20 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-bold text-[#2f3126]">
              Sources consultées — {llmName}
              {category && <span className="font-normal text-[#77726e]"> · {category}</span>}
            </h3>
            <p className="text-[0.7rem] text-[#77726e] mt-0.5">
              {groups.length} site{groups.length > 1 ? 's' : ''} · {totalPages} page{totalPages > 1 ? 's' : ''} · {totalRefs} référence{totalRefs > 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#77726e] hover:text-[#2f3126] text-base leading-none transition shrink-0 mt-0.5"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-3 space-y-1.5">
          {groups.map(([host, entries]) => (
            <HostGroup key={host} host={host} entries={entries} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default function LLMCard({ llm, category, categoryMedian = 50, categoryStddev = 0 }) {
  const [showWebOff, setShowWebOff] = useState(false)
  const [showUrlPopup, setShowUrlPopup] = useState(false)

  const data = llm?.categories?.[category] || {}
  const score = data.score ?? 0
  const severity = getScoreSeverity(score, categoryMedian, categoryStddev)

  const mode = showWebOff ? 'web_off' : 'web_on'
  const pros = data[`${mode}_pros`] || []
  const cons = data[`${mode}_cons`] || []

  const urlsMap = data.web_on_urls || {}
  const urlEntries = Object.entries(urlsMap)

  return (
    <>
      <div className="bg-white border border-[#77726e]/30 shadow-sm h-full flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 grid grid-cols-3 items-center border-b border-[#77726e]/15">
          <h3 className="text-sm font-bold text-[#2f3126] font-title min-w-0 truncate">
            {llm?.llm_name || 'Nom non disponible'}
          </h3>
          <div className="flex justify-center gap-0.5">
            <button
              onClick={() => setShowWebOff(false)}
              className={`px-2.5 py-1 text-[0.7rem] font-semibold border transition ${
                !showWebOff
                  ? 'bg-[#4e6498] text-white border-[#4e6498]'
                  : 'bg-[#f6f3ee] text-[#77726e] border-[#77726e]/30 hover:border-[#4e6498]/40'
              }`}
            >
              Avec web
            </button>
            <button
              onClick={() => setShowWebOff(true)}
              className={`px-2.5 py-1 text-[0.7rem] font-semibold border transition ${
                showWebOff
                  ? 'bg-[#4e6498] text-white border-[#4e6498]'
                  : 'bg-[#f6f3ee] text-[#77726e] border-[#77726e]/30 hover:border-[#4e6498]/40'
              }`}
            >
              Sans web
            </button>
          </div>
          <div className="flex justify-end">
            <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 border" style={severity}>
              {score}/100
            </span>
          </div>
        </div>

        {/* Content */}
        {(pros.length > 0 || cons.length > 0) && (
          <div className="px-5 py-3 flex-1 text-xs text-[#2f3126]">
            {pros.length > 0 && (
              <ul className="space-y-1">
                {pros.map((p, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <CheckCircle size={11} className="shrink-0 mt-1 text-[#39774f]" />
                    <span className="leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            )}
            {cons.length > 0 && (
              <ul className={`space-y-1${pros.length > 0 ? ' mt-5' : ''}`}>
                {cons.map((c, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <XCircle size={11} className="shrink-0 mt-1 text-[#9c2128]" />
                    <span className="leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Sources footer */}
        {!showWebOff && urlEntries.length > 0 && (
          <div className="px-5 py-2 border-t border-[#77726e]/15 flex justify-center">
            <button
              onClick={() => setShowUrlPopup(true)}
              className="px-2.5 py-1 text-[0.7rem] font-semibold border bg-[#f6f3ee] text-[#77726e] border-[#77726e]/30 hover:bg-[#edeae4] hover:border-[#4e6498]/40 transition"
            >
              Sources
            </button>
          </div>
        )}
      </div>

      {showUrlPopup && (
        <UrlPopup
          urls={urlsMap}
          llmName={llm?.llm_name}
          category={category}
          onClose={() => setShowUrlPopup(false)}
        />
      )}
    </>
  )
}
